import React from "react";
import injectSheet from "react-jss";

function calculateOffset(startingRotation) {
  let offset = 0;
  if (startingRotation) {
    let startingRot = startingRotation.y;
    startingRot = (startingRot * -1) / Math.PI / 2;
    if (startingRot < 0) {
      startingRot += 1;
    }
    offset = startingRot;
  }

  return offset;
}

function getBezierPoint(ratio, point, scale) {
  let { x, y } = point;

  if (ratio < 0) {
    ratio += 1;
  }

  if (ratio > 1) {
    ratio -= 1;
  }

  if (ratio < 0.125) {
    y -= scale;
  } else if (ratio === 0.125) {
    x += scale;
    y -= scale;
  } else if (ratio < 0.375) {
    x += scale;
  } else if (ratio === 0.375) {
    x += scale;
    y += scale;
  } else if (ratio < 0.625) {
    y += scale;
  } else if (ratio === 0.625) {
    x -= scale;
    y += scale;
  } else if (ratio < 0.875) {
    x -= scale;
  } else if (ratio === 0.875) {
    x -= scale;
    y -= scale;
  } else {
    y -= scale;
  }

  return { x, y };
}

function getBezierScale(startPt, endPt) {
  const length =
    (startPt.x - endPt.x) * (startPt.x - endPt.x) +
    (startPt.y - endPt.y) * (startPt.y - endPt.y);
  let scale = (length / (200 * 200)) * 100;
  if (scale < 20) {
    scale = 20;
  } else if (scale > 100) {
    scale = 100;
  }
  return scale;
}

class TourCanvasGrid extends React.Component {
  componentDidMount() {
    this.updateCanvas();
  }

  componentDidUpdate() {
    this.updateCanvas();
  }

  getPosition(image) {
    const { x, y, scale, gridUnit, canvasRect } = this.props;
    // Position before offsetting with canvas
    const imageLeft = (image.x + x) * gridUnit * scale;

    // Top is minus as the x,y follows coordinate convention
    // In CSS, 0,0 is top left rather than bottom left
    const imageTop = -(image.y + y) * gridUnit * scale;

    const { width, height } = canvasRect;
    const leftOffset = width / 2;
    const topOffset = height / 2;

    return {
      left: imageLeft + leftOffset,
      top: imageTop + topOffset,
    };
  }

  getXYfromOffsetAndRatio(position, ratio) {
    let { width, height } = this.props.canvasImageSize;
    width *= this.props.scale;
    height *= this.props.scale;

    const { top, left } = position;

    let x = 0;
    let y = 0;

    if (ratio < 0) {
      ratio += 1;
    }

    if (ratio > 1) {
      ratio -= 1;
    }

    if (ratio < 0.125) {
      y = top;
      x = left + (0.5 + (ratio / 0.125) * 0.5) * width;
    } else if (ratio < 0.375) {
      x = left + width;
      y = top + ((ratio - 0.125) / 0.25) * height;
    } else if (ratio < 0.625) {
      y = top + height;
      x = left + width - ((ratio - 0.375) / 0.25) * width;
    } else if (ratio < 0.875) {
      x = left;
      y = top + height - ((ratio - 0.625) / 0.25) * height;
    } else {
      y = top;
      x = left + ((ratio - 0.875) / 0.125) * 0.5 * width;
    }

    return { x, y };
  }

  updateCanvas() {
    const { width, height } = this.container.getBoundingClientRect();

    // Set the canvas to be same size as container
    this.canvas.width = width;
    this.canvas.height = height;

    const ctx = this.canvas.getContext("2d");

    const { x, y, scale, gridUnit, images } = this.props;

    // Starting positions
    // Fist fine where positions if x and y = 0
    const zeroX = (width / 2) % (gridUnit * scale);
    const zeroY = (height / 2) % (gridUnit * scale);

    // Then offset them by the x and y
    const startX = (zeroX + x * gridUnit * scale) % (gridUnit * scale);
    const startY = (zeroY - y * gridUnit * scale) % (gridUnit * scale);

    let currentX = startX;

    // Find how far we are from original center
    const xDistance = Math.round(x + (width / 2 - startX) / (gridUnit * scale));
    let count = -xDistance;

    while (currentX < width) {
      if (count % 10 === 0) {
        ctx.strokeStyle = "rgb(0, 0, 0, 0.4)";
      } else {
        ctx.strokeStyle = "rgb(0, 0, 0, 0.3)";
      }

      ctx.lineWidth = 1.5 * scale;
      ctx.beginPath();
      ctx.moveTo(currentX, 0);
      ctx.lineTo(currentX, height);
      ctx.stroke();

      currentX += gridUnit * scale;
      count += 1;
    }

    let currentY = startY;

    const yDistance = Math.round(
      y - (height / 2 - startY) / (gridUnit * scale),
    );
    count = yDistance;

    while (currentY < height) {
      if (count % 10 === 0) {
        ctx.strokeStyle = "rgb(0, 0, 0, 0.5)";
      } else {
        ctx.strokeStyle = "rgb(0, 0, 0, 0.4)";
      }

      ctx.beginPath();
      ctx.moveTo(0, currentY);
      ctx.lineTo(width, currentY);
      ctx.stroke();

      currentY += gridUnit * scale;

      count += 1;
    }

    // drawing node links
    const nodeLinkInfo = [];
    for (const image of images) {
      const nodelinks = image.hotspots;
      if (!nodelinks) {
        continue;
      }

      for (const link in nodelinks) {
        if (
          nodelinks[link].type !== "link" &&
          nodelinks[link].type !== "fade" &&
          nodelinks[link].type !== "elevate-up" &&
          nodelinks[link].type !== "elevate-down"
        )
          continue;

        const offset = calculateOffset(image.startingRotation);

        const trueOffset =
          offset -
          (nodelinks[link].options.addOffset
            ? nodelinks[link].options.offset || 0
            : 0);

        const targetImage = images.find(
          x => x.id === nodelinks[link].options.id,
        );
        if (!targetImage) continue;
        const targetOffset = calculateOffset(targetImage.startingRotation);

        const trueTargetOffset =
          targetOffset -
          (nodelinks[link].options.addOffset
            ? nodelinks[link].options.targetOffset || 0
            : 0);
        const nodelink = {
          id: image.id,
          ratio: nodelinks[link].ratio,
          target_id: nodelinks[link].options.id,
          target_ratio: nodelinks[link].options.ratio,
          type: nodelinks[link].options.type,
          trueOffset,
          trueTargetOffset,
          targetOffset,
          offset,
          nodeOff: nodelinks[link].options.offset,
          addOffset:
            nodelinks[link].options.addOffset ||
            nodelinks[link].addOffset ||
            false,
        };
        nodeLinkInfo.push(nodelink);
      }
    }

    if (nodeLinkInfo.length <= 0) {
      return;
    }

    for (let i = 0; i < nodeLinkInfo.length; i++) {
      const { id, type, trueOffset, trueTargetOffset, target_id } =
        nodeLinkInfo[i];
      const ratio = nodeLinkInfo[i].addOffset
        ? nodeLinkInfo[i].ratio - trueOffset
        : nodeLinkInfo[i].ratio;
      const target_ratio = nodeLinkInfo[i].addOffset
        ? nodeLinkInfo[i].target_ratio - trueTargetOffset
        : nodeLinkInfo[i].target_ratio;
      let position = this.getPosition(images.find(x => x.id === id));
      if (!position) {
        continue;
      }

      const startPt = this.getXYfromOffsetAndRatio(position, ratio);
      ctx.beginPath();
      ctx.arc(startPt.x, startPt.y, 10, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(214, 214, 212, 1)";
      ctx.fill();

      if (type === 0) {
        continue;
      }

      if (target_id === undefined) {
        continue;
      }

      position = this.getPosition(images.find(x => x.id === target_id));

      if (!position) {
        continue;
      }

      const endPt = this.getXYfromOffsetAndRatio(position, target_ratio);

      const bezierScale = getBezierScale(startPt, endPt);
      const bezierPt1 = getBezierPoint(ratio, startPt, bezierScale);
      const bezierPt2 = getBezierPoint(target_ratio, endPt, bezierScale);

      ctx.strokeStyle = "rgba(214, 214, 212, 1)";
      ctx.lineWidth = 3 * scale;
      ctx.beginPath();
      ctx.moveTo(startPt.x, startPt.y);
      ctx.bezierCurveTo(
        bezierPt1.x,
        bezierPt1.y,
        bezierPt2.x,
        bezierPt2.y,
        endPt.x,
        endPt.y,
      );
      ctx.stroke();

      if (type === 1) {
        continue;
      }
      ctx.beginPath();
      ctx.arc(endPt.x, endPt.y, 10, 0, 2 * Math.PI);
      ctx.fillStyle = "rgba(214, 214, 212, 1)";
      ctx.fill();
    }
  }

  render() {
    const { classes } = this.props;

    return (
      <div className={classes.container} ref={ref => (this.container = ref)}>
        <canvas
          className={classes.container}
          ref={ref => (this.canvas = ref)}
        />
      </div>
    );
  }
}

const styles = {
  container: {
    position: "absolute",
    top: 0,
    left: 0,
    width: "100%",
    height: "100%",
  },
};

export default injectSheet(styles)(TourCanvasGrid);
